// ImportarProdutos.jsx
import React, { useEffect, useState } from "react";
import { Card, Button, Typography, notification, Spin } from "antd";
import styled from "styled-components";
import * as XLSX from "xlsx";
import { useConfigQuery } from "../../../../queries/useConfig";
import axiosInstance from "../../../../axiosInterceptor";
import { cancelarTransacaoPendente, gerarTransacao, getTransacaoById } from "../../../Services/Funcoes";
import axios from "axios";

const { Title } = Typography;

/* ===== estilos ===== */
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  min-height: 100vh;
  background: #f0f2f5;
`;
const UploadCard = styled(Card)`
  width: 600px;
  margin-top: 50px;
  padding: 24px;
`;

/* Área de arrastar/soltar */
const DropZone = styled.div`
  margin-top: 12px;
  border: 2px dashed ${({ $dragging }) => ($dragging ? "#1677ff" : "#bbb")};
  background: ${({ $dragging }) => ($dragging ? "rgba(22, 119, 255, 0.06)" : "#fff")};
  border-radius: 10px;
  padding: 18px;
  text-align: center;
  color: #555;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  &:hover { border-color: #1677ff; }
`;

/* ===== colunas e mapeamento de cabeçalhos ===== */
const COLS = [
  { key: "id", header: "id" },
  { key: "nome", header: "nome" },
  { key: "descricao", header: "descricao" },
  { key: "preco", header: "preco" },
  { key: "custo", header: "custo" },
  { key: "codigo", header: "codigo" },
  { key: "ean", header: "ean" },
  { key: "unidade", header: "unidade" },
  { key: "categoria", header: "nome da categoria" },
  { key: "ncm", header: "ncm" },
  { key: "cfop", header: "cfop" },
  { key: "origem", header: "origem" },
  { key: "cst", header: "cst" },
  { key: "picms", header: "aliquota_icms" },
  { key: "predBc", header: "reducao_icms" },
  { key: "cstIpi", header: "cstIpi" },
  { key: "pipi", header: "aliquota_ipi" },
  { key: "cstPis", header: "cstPis" },
  { key: "ppis", header: "aliquota_pis" },
  { key: "cstCofins", header: "cstCofins" },
  { key: "pcofins", header: "aliquota_cofins" },
];
const headerToKeyBase = Object.fromEntries(COLS.map((c) => [c.header.trim().toLowerCase(), c.key]));
const HEADER_ALIASES = {
  "cst pis": "cstPis", "cst_pis": "cstPis", "cstpis": "cstPis",
  "cst ipi": "cstIpi", "cst_ipi": "cstIpi", "cstipi": "cstIpi",
  "cst cofins": "cstCofins", "cst_cofins": "cstCofins", "cstcofins": "cstCofins",
  "p pis": "ppis", "p_pis": "ppis", "aliquota pis": "ppis", "aliquota_pis": "ppis",
  "p cofins": "pcofins", "p_cofins": "pcofins", "aliquota cofins": "pcofins", "aliquota_cofins": "pcofins",
  "predbc": "predBc", "pre dbc": "predBc", "p_red_bc": "predBc", "reducao_icms": "predBc",
  "aliquota icms": "picms", "aliquota_icms": "picms", "p_icms": "picms",
  "categoria": "categoria", "nome da categoria": "categoria",
};
function mapHeaderToKey(rawHeader) {
  const norm = String(rawHeader || "").trim().toLowerCase();
  return headerToKeyBase[norm] || HEADER_ALIASES[norm] || null;
}

/* ===== utils ===== */
function parseNumberBR(value) {
  if (value == null || value === "") return "";
  if (typeof value === "number") return value;
  const s = String(value).replace(/\./g, "").replace(",", ".");
  const n = Number(s);
  return Number.isNaN(n) ? value : n;
}
function normalizeDigitCode(val) {
  if (val === null || val === undefined) return { ok: false, v: "" };
  if (typeof val === "number" && Number.isFinite(val)) {
    return { ok: true, v: String(Math.trunc(val)) };
  }
  const s = String(val).trim();
  if (/^\d+(\.0+)?$/.test(s)) return { ok: true, v: String(parseInt(s, 10)) };
  if (/^\d+$/.test(s)) return { ok: true, v: s };
  return { ok: false, v: s };
}
function linhaVaziaByHeaders(rowArr, headers) {
  for (let i = 0; i < headers.length; i++) {
    const v = rowArr[i];
    if (v != null && String(v).trim() !== "") return false;
  }
  return true;
}
function normalizarTiposNumericos(objApi) {
  const out = { ...objApi };
  const numKeys = ["preco", "custo", "picms", "predBc", "pipi", "ppis", "pcofins"];
  numKeys.forEach((k) => (out[k] = parseNumberBR(out[k])));
  return out;
}
function productLabel(o, idx) {
  const nome = (o?.nome || "").toString().trim();
  if (nome) return nome;
  const codigo = (o?.codigo || "").toString().trim();
  if (codigo) return `cód ${codigo}`;
  if (o?.id) return `id ${o.id}`;
  return `linha ${idx + 2}`;
}

/* ===== validação/ajustes ===== */
function validarEAjustar(items) {
  const errors = [];
  const ajustados = items.map((item, idx) => {
    const o = { ...item };
    const label = productLabel(o, idx);
    const row = idx + 2;

    if (!o.nome || String(o.nome).trim() === "") {
      errors.push(`Linha ${row} (Produto: ${label}): nome não pode ser em branco.`);
    }
    if (!o.descricao || String(o.descricao).trim() === "") {
      o.descricao = o.nome || "";
    }

    const precoNum = Number(o.preco);
    if (o.preco === "" || !Number.isFinite(precoNum) || precoNum <= 0) {
      errors.push(`Linha ${row} (Produto: ${label}): preço deve ser numérico e maior que zero.`);
    } else {
      o.preco = precoNum;
    }

    const custoNum = Number(o.custo);
    o.custo = (o.custo === "" || !Number.isFinite(custoNum)) ? 0 : custoNum;

    if (!o.ean || String(o.ean).trim() === "") o.ean = "SEM GTIN";

    if (!o.unidade || String(o.unidade).trim() === "") o.unidade = "UN";
    else o.unidade = String(o.unidade).toUpperCase();

    if (!o.ncm || String(o.ncm).trim() === "") {
      errors.push(`Linha ${row} (Produto: ${label}): NCM não pode ser em branco.`);
    }
    if (!o.cfop || String(o.cfop).trim() === "") {
      errors.push(`Linha ${row} (Produto: ${label}): CFOP não pode ser em branco.`);
    }
    if (o.origem == null || String(o.origem).trim() === "") o.origem = 0;

    let nd = normalizeDigitCode(o.cst);
    if (!nd.ok) errors.push(`Linha ${row} (Produto: ${label}): CST é obrigatório e deve conter apenas dígitos.`);
    else o.cst = nd.v;

    const picmsNum = Number(o.picms);
    o.picms = (o.picms === "" || !Number.isFinite(picmsNum)) ? 0 : picmsNum;

    const predBcNum = Number(o.predBc);
    o.predBc = (o.predBc === "" || !Number.isFinite(predBcNum)) ? 0 : predBcNum;

    const pipiNum = Number(o.pipi);
    o.pipi = (o.pipi === "" || !Number.isFinite(pipiNum)) ? 0 : pipiNum;

    nd = normalizeDigitCode(o.cstPis);
    if (!nd.ok) errors.push(`Linha ${row} (Produto: ${label}): CSTPIS é obrigatório e deve conter apenas dígitos.`);
    else o.cstPis = nd.v;

    const ppisNum = Number(o.ppis);
    o.ppis = (o.ppis === "" || !Number.isFinite(ppisNum)) ? 0 : ppisNum;

    nd = normalizeDigitCode(o.cstCofins);
    if (!nd.ok) errors.push(`Linha ${row} (Produto: ${label}): CSTCOFINS é obrigatório e deve conter apenas dígitos.`);
    else o.cstCofins = nd.v;

    const pcofinsNum = Number(o.pcofins);
    o.pcofins = (o.pcofins === "" || !Number.isFinite(pcofinsNum)) ? 0 : pcofinsNum;

    return o;
  });

  return { ajustados, errors };
}

/* ===== transforma no formato do ProdutoInput ===== */
function toProdutoInputPayload(o) {
  return {
    // incluir id quando vier na planilha (para UPDATE)
    id: o.id || "",
    nome: o.nome,
    descricao: o.descricao,
    preco: o.preco,
    custo: o.custo,
    ativo: true,
    codigo: o.codigo || "",
    ean: o.ean || "SEM GTIN",
    unidade: (o.unidade || "UN").toUpperCase(),

    // nomes exatamente como o backend espera (se o DTO estiver em lower)
    ncm: o.ncm,
    cfop: o.cfop,
    origem: String(o.origem ?? "0"),
    cst: o.cst,

    categoria: o.categoria || "geral",

    picms: o.picms,
    predBc: o.predBc,
    cstIpi: o.cstIpi || '',
    pipi: o.pipi,
    cstPis: o.cstPis,
    ppis: o.ppis,
    cstCofins: o.cstCofins,
    pcofins: o.pcofins,
  };
}

/* ===== componente ===== */
export default function ImportarProdutos() {
  const accessToken = window.localStorage.getItem("accessToken");
  const [processamentoMsg, setProcessamentoMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const { data: config } = useConfigQuery();
  const [fileName, setFileName] = useState("");
  const [dragging, setDragging] = useState(false);
  const [transactionId, setTransactionId] = useState('');
  const [executarBusca, setExecutarBusca] = useState(false);

  const onPickFile = () => document.getElementById("xlsx-input")?.click();

  // Valida extensão de arquivo
  const isExcelFile = (f) => {
    if (!f) return false;
    const name = (f.name || "").toLowerCase();
    return name.endsWith(".xlsx") || name.endsWith(".xls");
  };






  useEffect(() => {
    async function fetchData() {
      console.log('dentro do fetchData');
      try {
        var cont = 0;
        while (cont < 6) {
          await new Promise((resolve) => setTimeout(resolve, 10000));
          const options = {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + accessToken,
            },
          };

          console.log('getTransacaoById');
          const { data: result } = await getTransacaoById(transactionId, config.idLoja);


          console.log(result);
          //setTransactionId(result);

          if (result.status === '2') {
            notification.info({
              message: 'Informação',
              description: 'As alterações estão sendo processadas.',
            });
          }
          if (result.status === '3') {
            setExecutarBusca(false);
            //handleRefreshClick();
            notification.success({
              message: 'Sucesso',
              description: 'As alterações foram concluidas.',
            });

            cont = 7;
            break;
          }
          cont++;
        }

        if (cont === 6) {
          if (
            window.confirm(
              'Deseja cancelar o processamento?\nClique Ok para cancelar'
            ) === true
          ) {
            await cancelarTransacaoPendente(config.idLoja);
            cont = 0;
          } else {
            cont = 0;
          }

          cont = 0;
        }
      } catch (error) {
        console.error(error);
      }
    }

    if (config.idLoja && transactionId && executarBusca) {
      fetchData();
    }
  }, [config.idLoja, transactionId, executarBusca]);













  // Lê/processa o arquivo (usado por input e por drop)
  const handleFile = async (file) => {
    setLoading(true);
    setProcessamentoMsg('Processando a planilha aguarde...');
    if (!isExcelFile(file)) {
      notification.error({
        message: "Arquivo inválido",
        description: "Envie um arquivo .xlsx ou .xls.",
      });
      return;
    }

    try {
      setFileName(file.name);
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      if (!ws) throw new Error("Planilha sem aba 1.");

      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      if (!aoa.length) throw new Error("Planilha vazia.");
      const headersRaw = aoa[0] || [];

      const idxToKey = {};
      const notMapped = [];
      headersRaw.forEach((h, i) => {
        const keyApi = mapHeaderToKey(h);
        if (keyApi) idxToKey[i] = keyApi;
        else notMapped.push(String(h));
      });

      const expected = COLS.map((c) => c.key);
      const found = new Set(Object.values(idxToKey));
      const missing = expected.filter((k) => !found.has(k));
      if (missing.length) {
        notification.error({
          message: "Colunas faltando ou com nome inesperado",
          description: (
            <div>
              <div>Faltando (ou não reconhecidas): <b>{missing.join(", ")}</b></div>
              {notMapped.length > 0 && (
                <div style={{ marginTop: 8 }}>Cabeçalhos não reconhecidos: {notMapped.join(", ")}</div>
              )}
            </div>
          ),
          duration: 10,
        });
        return;
      }

      const dataRows = aoa.slice(1).filter((row) => !linhaVaziaByHeaders(row, headersRaw));

      const itensApiPrimarios = dataRows.map((rowArr) => {
        const o = {};
        Object.keys(idxToKey).forEach((idx) => {
          const key = idxToKey[idx];
          o[key] = rowArr[idx];
        });
        return normalizarTiposNumericos(o);
      });

      const { ajustados, errors } = validarEAjustar(itensApiPrimarios);

      if (ajustados.length > 500) {
        notification.error({
          message: "Máximo de 500 produtos por operação",
          duration: 12,
        });
        setProcessamentoMsg('');
        setLoading(false);
        return;
      }

      if (errors.length) {
        const max = 8;
        notification.error({
          message: "Erros de validação",
          description: (
            <div>
              <div>Corrija os itens abaixo e importe novamente:</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {errors.slice(0, max).map((e, i) => <li key={i}>{e}</li>)}
              </ul>
              {errors.length > max && <div>+{errors.length - max} outros erros…</div>}
            </div>
          ),
          duration: 12,
        });
        setProcessamentoMsg('');
        setLoading(false);
        return;
      }

      //  const payloadCompat = ajustados.map(toProdutoInputPayload);


      // if (console.table) console.table(payloadCompat);

      notification.success({
        message: "Importação validada",
        description: `Lidas ${ajustados.length} linhas de ${file.name}. Payload compatível gerado no console.`,
      });

      setProcessamentoMsg('Cadastrando ou Atualizando Produtos...');

      const transacao = await gerarTransacao(config.idLoja)
      notification.success({ message: "transação criada: " + transacao });
      let cont = 0;
      let transactionInfo;

      const options = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + accessToken,
        },
      };
      do {
        //axios.put(`https://techspdv-api-d3e739785e34.herokuapp.com/v1/movimentacaoEstoque/editarLista/${config.idLoja}/${response.data}/compra/${total}/${idUser}`, novoObjeto, options);

        axiosInstance.put(
          // `http://localhost:8077/v1/restaurantes/${config.idLoja}/produtos/planilhaDeProdutos/${transacao}`,
          `https://techfood-api.herokuapp.com/v1/restaurantes/${config.idLoja}/produtos/planilhaDeProdutos/${transacao}`,

          ajustados,
         options
        );

        setProcessamentoMsg('');
        setLoading(false);

        if ((config.idLoja, transacao)) {
          transactionInfo = await getTransacaoById(transacao, config.idLoja);
          console.log(transactionInfo.data);


          if (transactionInfo.data.status !== '1') {
            console.log(transactionInfo.data);
            setTransactionId(transacao);
            setExecutarBusca(true);
            //    setProdutos([]);
            notification.info({
              message: 'Informação',
              description: 'As alterações estão sendo processadas.',
            });

          } else {
            await new Promise((resolve) => setTimeout(resolve, 10000));
            cont++;
            console.log(cont + 'tentativa');
            if (cont === 6) {
              setLoading(false);
              notification.error({
                message: 'Erro',
                description:
                  'Não foi possível o envio das alterações, tente novamente mais tarde.',
              });
            }
          }
        }
      } while (transactionInfo.data.status === '1' && cont < 6);
    } catch (error) {
      if (error.response && error.response.status === 409) {
        notification.info({
          message: 'Informação',
          description: 'Já existe uma transação ativa para essa loja nos últimos 30 segundos.',
        });
      } else {
        console.error('Erro ao criar transação:', error);
        // outros tipos de erro
      }
    }



    setLoading(false);
    return;

  }


  // Input file (clique)
  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    await handleFile(file);
  };

  // Drag & Drop handlers
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setDragging(true);
  };
  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };
  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    const files = e.dataTransfer?.files;
    if (!files || !files.length) return;
    await handleFile(files[0]);
  };

  return (
    <Container>
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 40, width: '100%' }}>
          <Spin
            spinning={loading}
            tip={<span style={{ fontSize: 16, fontWeight: '500', color: '#333' }}>Carregando...</span>}
            size="large"
          />
          {processamentoMsg && (
            <span style={{ marginTop: 16, fontSize: 14, color: '#666' }}>{processamentoMsg}</span>
          )}
        </div>
      ) : null}
      {!loading &&
        <UploadCard>
          <Title level={3}>IMPORTAR PRODUTOS</Title>

          {/* input oculto para clique */}
          <input id="xlsx-input" type="file" accept=".xlsx,.xls" onChange={onFileChange} hidden />

          <Button
            type="primary"
            block
            onClick={onPickFile}
            style={{ backgroundColor: "black", borderColor: "black", color: "#fff" }}
          >
            Selecionar arquivo XLSX
          </Button>

          {/* DROPZONE: clique ou arraste o arquivo */}
          <DropZone
            $dragging={dragging}
            onClick={onPickFile}
            onDragOver={onDragOver}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onPickFile()}
            aria-label="Solte aqui o arquivo XLSX para importar"
          >
            Arraste e solte o arquivo .xlsx aqui
            <div style={{ fontSize: 12, marginTop: 6 }}>ou clique para selecionar</div>
          </DropZone>

          <div style={{ marginTop: 12, color: "#666", fontSize: 12 }}>
            {fileName ? `Selecionado: ${fileName}` : "Use o XLSX no formato do export (id, nome, descricao, preco, ...)."}
          </div>
          <div style={{ marginTop: 12, color: "#666", fontSize: 12 }}>
            {"Máximo de 500 produtos por operação..."}
          </div>

        </UploadCard>}
    </Container>
  );

}